import { useState, useRef } from 'react';
import { api } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

interface RecordingButtonProps {
  phraseId: string;
  assignmentId: string;
  customPhraseId?: string;
  onComplete: () => void;
}

export function RecordingButton({ phraseId, assignmentId, customPhraseId, onComplete }: RecordingButtonProps) {
  const { t } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const startRecording = async () => {
    setError('');
    setSuccess(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try different MIME types
      const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      let selectedMimeType = '';

      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType || undefined
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setRecordingTime(0);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: selectedMimeType || 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current!);

        // Upload
        setIsUploading(true);
        try {
          await api.uploadRecording(blob, phraseId, assignmentId, customPhraseId);
          setSuccess(true);
          onComplete();
        } catch (err: any) {
          setError(err.message || 'Failed to upload recording');
        } finally {
          setIsUploading(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);

    } catch (err: any) {
      console.error('Failed to start recording:', err);
      setError('Please allow microphone access to record');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="text-5xl mb-4">🎉</div>
        <p className="text-green-600 font-semibold text-lg">
          {t('recordingSuccess')}
        </p>
        <button
          className="btn btn-secondary mt-4"
          onClick={() => setSuccess(false)}
        >
          {t('recordAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="text-center">
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-col items-center gap-4">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isUploading}
          className={`recording-btn ${isRecording ? 'recording' : ''}`}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isUploading ? (
            <span className="animate-spin">⏳</span>
          ) : isRecording ? (
            <span>⏹️</span>
          ) : (
            <span>🎙️</span>
          )}
        </button>

        <div className="text-center">
          {isUploading ? (
            <p className="text-gray-600">{t('uploading')}</p>
          ) : isRecording ? (
            <>
              <p className="text-red-600 font-semibold text-xl">
                {formatTime(recordingTime)}
              </p>
              <p className="text-gray-600 text-sm">{t('recording')}</p>
            </>
          ) : (
            <p className="text-gray-600">{t('tapToRecord')}</p>
          )}
        </div>
      </div>

      {!isRecording && !isUploading && (
        <div className="mt-6 p-4 bg-indigo-50 rounded-xl">
          <p className="text-indigo-700 text-sm">
            {t('speakTip')}
          </p>
        </div>
      )}
    </div>
  );
}
